// Shared helpers untuk modul Billing V1 (Phase 3 UI).

export function formatRupiah(n) {
  const v = Number(n || 0);
  return "Rp" + v.toLocaleString("id-ID");
}

export function hasBillingRole(user, ...roles) {
  if (!user) return false;
  if (roles.length === 0) roles = ["admin", "billing", "kolektor"];
  const prim = user.role;
  const extras = user.extra_roles || [];
  return roles.includes(prim) || roles.some((r) => extras.includes(r));
}

export const isAdmin = (u) => u?.role === "admin";
export const isBilling = (u) => hasBillingRole(u, "billing");
export const isKolektor = (u) => hasBillingRole(u, "kolektor");

// Feb 2026 — Landing page pasca-login berbeda per primary role.
// Kolektor tidak punya akses Dashboard Operasional ("/"), jadi diarahkan
// langsung ke halaman "Tagihan Saya" (BillingKolektor). Role lain default ke "/".
export function landingPathFor(user) {
  if (user?.role === "kolektor") return "/billing/tagihan-saya";
  return "/";
}

// Feb 2026 — Whitelist prefix route yang boleh diakses primary role
// kolektor. Digunakan oleh ProtectedRoute untuk redirect anti-manual-URL
// (kolektor mengetik "/" → auto redirect ke landing). Backend RBAC tetap
// sumber kebenaran (billing_read/write scoped).
export const KOLEKTOR_ROUTE_PREFIXES = [
  "/billing",       // dashboard billing, tagihan-saya, customers, invoices, rekap (masters di-block terpisah oleh backend billing_manage)
  "/attendance",    // /attendance + /attendance-requests + /attendance-report
  "/overtime",
  "/settings",      // Akun & Password
];

export function isKolektorAllowedPath(pathname) {
  if (!pathname) return false;
  // /billing/masters butuh billing_manage → tetap izinkan render, backend
  // yang menolak akses. UI juga sudah tidak menampilkan menu untuk kolektor.
  return KOLEKTOR_ROUTE_PREFIXES.some((p) =>
    pathname === p || pathname.startsWith(p + "/") || pathname.startsWith(p + "?")
  );
}

// Feb 2026 — Billing MANAGEMENT hanya ADMIN. extra_roles=billing tidak
// lagi bypass manage. UI juga refleksikan ini (menu Master Billing hanya
// muncul untuk admin).
export const canBillingManage = (u) => isAdmin(u);

// Diskon & rollback & cancel invoice = admin only
export const canBillingAdminOnly = (u) => isAdmin(u);

export const STATUS_LABEL = {
  BELUM_LUNAS: "Belum Lunas",
  SEBAGIAN: "Dibayar Sebagian",
  LUNAS: "Lunas",
  DIBATALKAN: "Dibatalkan",
};

// Feb 2026 — Phase 13: label "Diterima Oleh" berdasarkan payment doc.
// Payment lama (pre-Phase 13) tidak punya received_role → fallback ke
// derivasi method + recorded_by. Kembalikan struktur { label, role }.
// Feb 2026 — Phase 15: display label untuk payment method. Value internal
// tetap "KANTOR" (backward compat DB & backend); UI menampilkan "LOKET".
export function methodLabel(method) {
  if (method === "KANTOR") return "LOKET";
  return method || "";
}

export function receivedLabel(p) {
  if (!p) return { label: "-", role: null };
  const role = p.received_role || null;
  const name = p.received_by_name_snapshot || "";
  if (role === "kolektor" && name) return { label: `Kolektor — ${name}`, role };
  if (role === "admin" && name) return { label: `Admin/Loket — ${name}`, role };
  // Fallback deterministic dari method (backward compat payment lama).
  const m = p.method || "";
  if (m === "TRANSFER") return { label: "Admin/Perusahaan", role: null };
  if (m === "KANTOR") return { label: "Admin/Loket", role: "admin" };
  if (m === "TUNAI") return { label: name ? `Kolektor — ${name}` : "Kolektor (tidak teridentifikasi)", role: "kolektor" };
  return { label: "-", role: null };
}

export const STATUS_COLOR = {
  BELUM_LUNAS: "bg-[#FEE2E2] text-[#991B1B]",
  SEBAGIAN: "bg-[#FEF3C7] text-[#92400E]",
  LUNAS: "bg-[#D1FAE5] text-[#065F46]",
  DIBATALKAN: "bg-[#E5E7EB] text-[#374151]",
};

export const CUST_STATUS_LABEL = {
  AKTIF: "Aktif",
  ISOLIR: "Isolir",
  BERHENTI_SEMENTARA: "Berhenti Sementara",
  BERHENTI: "Berhenti",
  FREE: "Free",
};

export function periodeLabel(yyyyMM) {
  if (!yyyyMM) return "-";
  const [y, m] = yyyyMM.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
  return `${names[parseInt(m, 10) - 1] || m} ${y}`;
}
